import { CctvResponse } from 'modules/maps';
import { useEffect, useRef } from 'react';
import {
  createMarkerContainer,
  createMarkerImg,
  createLinkBadge,
  createTooltip,
  showTooltip,
  hideTooltip
} from '../utils.ts';
import { ptwMapImages } from '../../../../../../../constants';

type CctvMarkerProps = {
  map: google.maps.Map | null;
  isLoaded: boolean;
  cctvList: CctvResponse[];
  onSelectCctv?: (cctv: CctvResponse | null) => void;
};

const ZONE_LABEL_PATTERN = /^S[1-9]$/;

function createZoneCircleMarker(label: string): HTMLElement {
  const size = 23;
  const el = document.createElement('div');
  el.style.position = 'absolute';
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.transform = 'translate(-50%, -50%)';
  el.style.display = 'flex';
  el.style.alignItems = 'center';
  el.style.justifyContent = 'center';
  el.style.borderRadius = '50%';
  el.style.backgroundColor = '#055CB4';
  el.style.border = '2px solid #ffffff';
  el.style.color = '#ffffff';
  el.style.fontWeight = 'bold';
  el.style.fontSize = '11px';
  el.style.fontFamily = 'sans-serif';
  el.style.cursor = 'pointer';
  el.style.userSelect = 'none';
  el.style.overflow = 'visible';
  el.textContent = label;
  return el;
}

export function CctvMarker({ map, isLoaded, cctvList, onSelectCctv }: CctvMarkerProps) {
  const onSelectCctvRef = useRef(onSelectCctv);
  onSelectCctvRef.current = onSelectCctv;

  useEffect(() => {
    if (!isLoaded || !map) return;
    if (!google.maps.marker?.AdvancedMarkerElement) return;

    const mapDiv = map.getDiv();
    const tooltip = createTooltip();
    mapDiv.appendChild(tooltip);

    const markers: google.maps.marker.AdvancedMarkerElement[] = [];

    cctvList
      .filter((cctv) => {
        const lat = Number(cctv.latitude);
        const lng = Number(cctv.longitude);
        return Number.isFinite(lat) && Number.isFinite(lng);
      })
      .forEach((cctv) => {
        const isZoneMarker = ZONE_LABEL_PATTERN.test(cctv.cctvName);
        const container = isZoneMarker
          ? createZoneCircleMarker(cctv.cctvName)
          : (() => {
              const c = createMarkerContainer(40);
              c.appendChild(createMarkerImg(ptwMapImages.cctv, cctv.cctvName, 40));
              return c;
            })();
        const linkBadge = createLinkBadge(cctv.ptwNo, ptwMapImages.link);
        if (linkBadge) container.appendChild(linkBadge);

        const tooltipRows = [
          { label: 'Device Name', value: cctv.cctvName },
          { label: 'ID', value: cctv.seq },
          ...(cctv.ptwNo ? [{ label: 'PTW', value: cctv.ptwNo }] : [])
        ];

        container.addEventListener('mouseenter', (e: MouseEvent) => {
          const rect = mapDiv.getBoundingClientRect();
          showTooltip(tooltip, tooltipRows, e.clientX - rect.left, e.clientY - rect.top);
        });
        container.addEventListener('mousemove', (e: MouseEvent) => {
          const rect = mapDiv.getBoundingClientRect();
          showTooltip(tooltip, tooltipRows, e.clientX - rect.left, e.clientY - rect.top);
        });
        container.addEventListener('mouseleave', () => hideTooltip(tooltip));

        const marker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: { lat: Number(cctv.latitude), lng: Number(cctv.longitude) },
          content: container,
          gmpClickable: true
        });

        const handler = () => {
          if (!cctv.url) {
            console.warn('CCTV streaming URL is not set:', cctv);
            return;
          }
          onSelectCctvRef.current?.(cctv);
        };

        marker.addEventListener('gmp-click', handler);

        markers.push(marker);
      });

    return () => {
      markers.forEach((m) => {
        m.map = null;
      });
      tooltip.remove();
    };
  }, [map, isLoaded, cctvList]);
  return null;
}
